import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { DollarSign, Info, TrendingUp, Mail, AlertTriangle, Target, Loader2 } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { useLeadCaptureOnboarding, MonthlyValueReport } from '../../hooks/useLeadCaptureOnboarding';

type OutletCtx = {
  userData?: { companyId?: string; role?: string };
};

// Mockup §8: Cost Comparison. Pure presentation — no backend.
// Figures mirror the mockup. Update here when pricing changes, not at
// runtime — this is marketing copy, not live data.

interface LineItem {
  label: string;
  price: string;
  note: string;
}

const CURRENT_STACK: LineItem[] = [
  {
    label: 'HubSpot (Marketing Hub)',
    price: '$2,200/mo',
    note: 'Nobody using it — zero leads going in',
  },
  {
    label: 'Zapier (proposed)',
    price: '$73/mo',
    note: 'Needed for round-robin assignment',
  },
  {
    label: 'Social Garden Phase 1',
    price: '$683/mo',
    note: '$8,190 one-off amortised over 12 months',
  },
  {
    label: 'Social Garden Phase 2 (est.)',
    price: '$1,250-2,500/mo',
    note: '$15-30K for bi-directional sync that MyCRM API cannot support',
  },
  {
    label: 'Lead attribution',
    price: '—',
    note: 'Not possible — no system connects ad spend to settlements',
  },
  {
    label: 'Cost per customer tracking',
    price: '—',
    note: 'Not possible — nobody has this data',
  },
];

const VALUTTEN_STACK: LineItem[] = [
  {
    label: 'Lead Capture Add-On',
    price: '$249/mo',
    note: 'Webhooks, attribution, email automation, MyCRM push — all included',
  },
  {
    label: 'Resend Pro (email sending)',
    price: '$28/mo',
    note: '50,000 emails/mo. $20 USD at $1.40 AUD rate',
  },
  {
    label: 'Lead capture (webhooks)',
    price: '$0',
    note: 'Direct from WordPress, Facebook, Google, Instagram — included',
  },
  {
    label: 'Lead attribution & ROI',
    price: '$0',
    note: 'Full lead-to-settlement-to-commission tracking — included',
  },
  {
    label: 'Cost per customer',
    price: '$0',
    note: 'Ad spend vs commission earned — included',
  },
  {
    label: 'Lead to settlement time',
    price: '$0',
    note: 'Average days from first contact to deal settled — included',
  },
  {
    label: 'MyCRM lead push',
    price: '$0',
    note: 'Auto-write leads to MyCRM via API — included',
  },
  {
    label: 'Email sequences & automation',
    price: '$0',
    note: 'Built into Valutten, sends via Resend — included',
  },
];

interface ReplaceRow {
  capability: string;
  currently: string;
  withValutten: string;
  status: 'replaces' | 'new';
}

const REPLACE_TABLE: ReplaceRow[] = [
  {
    capability: 'Lead capture from website forms',
    currently: 'HubSpot ($2,200/mo)',
    withValutten: 'Valutten webhooks (included)',
    status: 'replaces',
  },
  {
    capability: 'Lead capture from Facebook / Instagram',
    currently: 'HubSpot + Zapier',
    withValutten: 'Direct Meta webhook (included)',
    status: 'replaces',
  },
  {
    capability: 'Lead capture from Google Ads',
    currently: 'Not connected',
    withValutten: 'Direct Google webhook (included)',
    status: 'replaces',
  },
  {
    capability: 'Round-robin lead assignment',
    currently: 'Zapier ($73/mo)',
    withValutten: 'Cloud Function (included)',
    status: 'replaces',
  },
  {
    capability: 'Push leads to MyCRM',
    currently: 'Not connected',
    withValutten: 'MyCRM /jsonapi/leads (included)',
    status: 'replaces',
  },
  {
    capability: 'Email sequences & automation',
    currently: 'HubSpot (not being used)',
    withValutten: 'Valutten + Resend ($28/mo)',
    status: 'replaces',
  },
  {
    capability: 'Lead source attribution',
    currently: 'Not possible',
    withValutten: 'Full UTM tracking (included)',
    status: 'new',
  },
  {
    capability: 'Cost per customer',
    currently: 'Not possible',
    withValutten: 'Ad spend → settlement → commission (included)',
    status: 'new',
  },
  {
    capability: 'Lead to settlement time',
    currently: 'Not possible',
    withValutten: 'Automatic tracking (included)',
    status: 'new',
  },
  {
    capability: 'Trail leakage detection',
    currently: 'Not possible',
    withValutten: 'CRM settled vs commission trail (included)',
    status: 'new',
  },
  {
    capability: 'Broker productivity / call log',
    currently: '22-tab Google Sheet',
    withValutten: 'Digital call list + weekly funnel (included)',
    status: 'new',
  },
  {
    capability: 'Client engagement alerts',
    currently: 'Not possible',
    withValutten: '6mo warning / 12mo critical (included)',
    status: 'new',
  },
];

const PRICING_NOTES: string[] = [
  'Resend Pro: $20 USD/mo for 50,000 transactional emails. Source: resend.com/pricing',
  'USD to AUD rate: 1.40 (RBA mid-market, April 2026). $20 USD = $28 AUD',
  'Lead Capture add-on: $249 AUD/mo. Founding subscriber rate: $149 AUD/mo (first 3 companies)',
  'Team Activity add-on: $249 AUD/mo. Can be purchased independently of Lead Capture.',
  '90-day free trial available for case-study customers. Trial cost: $28 AUD/mo (Resend only)',
  'No separate marketing plan from Resend required — transactional API sends all email types.',
];

const StackCard: React.FC<{
  title: string;
  badge: string;
  badgeVariant: 'error' | 'ok';
  items: LineItem[];
  total: string;
}> = ({ title, badge, badgeVariant, items, total }) => {
  const titleColor = badgeVariant === 'error' ? 'text-red-300' : 'text-emerald-300';
  const border = badgeVariant === 'error' ? 'border-red-500/30' : 'border-emerald-500/30';
  const badgeCls =
    badgeVariant === 'error'
      ? 'bg-red-500/15 text-red-300 border-red-500/30'
      : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
  const totalCls = badgeVariant === 'error' ? 'text-red-300' : 'text-emerald-300';
  return (
    <div className={`rounded-lg border ${border} bg-gray-800/40 p-5`}>
      <div className="flex items-start justify-between mb-4">
        <h3 className={`text-sm font-bold ${titleColor}`}>{title}</h3>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badgeCls}`}>
          {badge}
        </span>
      </div>
      <ul className="space-y-3">
        {items.map((item) => (
          <li key={item.label} className="text-xs">
            <div className="flex items-start justify-between gap-3">
              <span className="text-gray-200 font-medium">{item.label}</span>
              <span className="text-gray-400 font-mono whitespace-nowrap">
                {item.price}
              </span>
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">{item.note}</div>
          </li>
        ))}
      </ul>
      <div className={`mt-4 pt-3 border-t border-gray-700 flex items-center justify-between`}>
        <span className="text-xs uppercase tracking-wider text-gray-500">
          Total
        </span>
        <span className={`text-lg font-bold ${totalCls}`}>{total}</span>
      </div>
    </div>
  );
};

const LeadCaptureCostSavingsPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId;
  // Trail-leakage is commission-derived; the backend strips it for marketing,
  // so omit the trail-leakage savings/metric surfaces here too (marketing
  // RBAC §6 cost-savings sanitization).
  const isMarketing = userData?.role === 'marketing';
  const { getMonthlyValueReport } = useLeadCaptureOnboarding(companyId);
  const [report, setReport] = useState<MonthlyValueReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setReportLoading(true);
    getMonthlyValueReport()
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((err) => {
        if (!cancelled) setReportError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setReportLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, getMonthlyValueReport]);

  const monthLabel = report
    ? new Date(report.monthStart).toLocaleDateString('en-AU', {
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={DollarSign}
        title="Cost Comparison"
        subtitle="Current stack vs Valutten platform — verified pricing as of April 2026"
      />

      {/* Your actual usage this month — live data */}
      <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white">
            Your actual usage this month
          </h3>
          <span className="text-[11px] text-gray-500">{monthLabel ?? '—'}</span>
        </div>
        {reportLoading && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {reportError && (
          <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-300">
            {reportError}
          </div>
        )}
        {!reportLoading && !reportError && report && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-md border border-gray-700 bg-gray-900/40 p-3">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                <TrendingUp className="w-3 h-3" /> Leads captured
              </div>
              <div className="text-2xl font-bold text-white">
                {report.leadsCount.toLocaleString('en-AU')}
              </div>
            </div>
            <div className="rounded-md border border-gray-700 bg-gray-900/40 p-3">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                <Mail className="w-3 h-3" /> Emails sent
              </div>
              <div className="text-2xl font-bold text-white">
                {report.emailsSentCount.toLocaleString('en-AU')}
              </div>
            </div>
            {/* Trail-leakage alerts are commission-derived, so hidden for
                marketing (backend strips the metric). */}
            {!isMarketing && (
              <div className="rounded-md border border-gray-700 bg-gray-900/40 p-3">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                  <AlertTriangle className="w-3 h-3" /> Trail leakage alerts
                </div>
                <div className="text-2xl font-bold text-white">
                  {report.alertsCaughtCount.toLocaleString('en-AU')}
                </div>
              </div>
            )}
            <div className="rounded-md border border-gray-700 bg-gray-900/40 p-3">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                <Target className="w-3 h-3" /> Attribution coverage
              </div>
              <div className="text-2xl font-bold text-white">
                {report.attributionCoveragePercent}%
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Current vs Valutten */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StackCard
          title="Current Stack"
          badge="$2,273 AUD/mo"
          badgeVariant="error"
          items={CURRENT_STACK}
          total="$2,273+/mo"
        />
        <StackCard
          title="Valutten Platform"
          badge="$277 AUD/mo"
          badgeVariant="ok"
          items={VALUTTEN_STACK}
          total="$277/mo"
        />
      </div>

      {/* Annual savings callout */}
      <div className="rounded-lg border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 p-8 text-center">
        <div className="text-[11px] uppercase tracking-wider text-gray-400">
          Annual savings
        </div>
        <div className="mt-2 text-5xl font-bold text-emerald-400">$23,952</div>
        <div className="mt-3 text-sm text-gray-300">
          $2,273/mo current → $277/mo with Valutten = $1,996/mo saved × 12 months
        </div>
        <div className="mt-2 text-sm text-emerald-300 font-semibold">
          88% cost reduction + better data than HubSpot ever provided
        </div>
      </div>

      {/* What Valutten replaces */}
      <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-700">
          <h3 className="text-sm font-semibold text-white">
            What Valutten replaces
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-900/50 text-gray-400 uppercase">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Capability</th>
                <th className="px-4 py-3 text-left font-medium">Currently</th>
                <th className="px-4 py-3 text-left font-medium">With Valutten</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {REPLACE_TABLE.filter(
                // Trail-leakage detection is commission-derived, so omit the
                // capability row for marketing (marketing RBAC §6).
                (row) =>
                  !(isMarketing && row.capability === 'Trail leakage detection')
              ).map((row) => (
                <tr key={row.capability} className="text-gray-200">
                  <td className="px-4 py-3 font-medium text-white">
                    {row.capability}
                  </td>
                  <td className="px-4 py-3 text-gray-400">{row.currently}</td>
                  <td className="px-4 py-3">{row.withValutten}</td>
                  <td className="px-4 py-3">
                    {row.status === 'replaces' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                        Replaces
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/15 text-blue-300 border border-blue-500/30">
                        New capability
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pricing verification */}
      <div className="rounded-lg border-l-4 border-l-blue-500 border-t border-r border-b border-gray-700 bg-gray-800/40 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm font-semibold text-white">
            Pricing verified April 2026
          </h3>
        </div>
        <ul className="space-y-1.5 text-xs text-gray-400">
          {PRICING_NOTES.map((note, idx) => (
            <li key={idx} className="flex gap-2">
              <span className="text-gray-600 flex-shrink-0">•</span>
              <span>{note}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default LeadCaptureCostSavingsPage;
